import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  Building2,
  CalendarDays,
  ChevronRight,
  Copy,
  Loader2,
  RefreshCw,
  Share2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  buildCompanyRequisitesTextBlocks,
  LEGAL_ENTITY_LABELS,
  type CompanyRequisitesTextBlocks,
} from '@shared/types';
import { companyRequisitesApi, type CompanyRequisites } from '@/api/company-requisites';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { CompanyRequisitesAssetsSection } from './CompanyRequisitesAssetsSection';
import { selectMobileCompanyRequisites } from './companyRequisitesViewModel';

type TextBlockKey = keyof CompanyRequisitesTextBlocks;

interface TextBlockDefinition {
  key: TextBlockKey;
  title: string;
  copyLabel: string;
  shareLabel: string;
}

const TEXT_BLOCKS: readonly TextBlockDefinition[] = [
  {
    key: 'requisites',
    title: 'Реквизиты',
    copyLabel: 'Копировать реквизиты',
    shareLabel: 'Отправить реквизиты',
  },
  {
    key: 'representative',
    title: 'Представитель',
    copyLabel: 'Копировать представителя',
    shareLabel: 'Отправить представителя',
  },
  {
    key: 'nominative',
    title: 'Руководитель в именительном падеже',
    copyLabel: 'Копировать руководителя в именительном падеже',
    shareLabel: 'Отправить руководителя в именительном падеже',
  },
];

function formatEffectiveDate(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(value));
}

function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as {
      response?: { data?: { message?: string | string[] } };
    }).response;
    const message = response?.data?.message;
    if (Array.isArray(message)) return message.join(', ');
    if (message) return message;
  }
  return error instanceof Error ? error.message : fallback;
}

async function writeTextToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Some webviews expose Clipboard API but reject the call. Fall back to
      // the selection-based browser command below.
    }
  }

  const previouslyFocused = document.activeElement instanceof HTMLElement
    ? document.activeElement
    : null;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.readOnly = true;
  textarea.setAttribute('aria-hidden', 'true');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);

  try {
    textarea.focus();
    textarea.select();
    if (typeof document.execCommand !== 'function' || !document.execCommand('copy')) {
      throw new Error('Clipboard API is unavailable');
    }
  } finally {
    textarea.remove();
    previouslyFocused?.focus({ preventScroll: true });
  }
}

function isAbortError(error: unknown): boolean {
  return Boolean(
    error
      && typeof error === 'object'
      && 'name' in error
      && (error as { name?: unknown }).name === 'AbortError',
  );
}

async function copyText(text: string): Promise<void> {
  try {
    await writeTextToClipboard(text);
    toast.success('Скопировано');
  } catch {
    toast.error('Не удалось скопировать');
  }
}

async function shareText(title: string, text: string): Promise<void> {
  if (!navigator.share) {
    return;
  }

  try {
    await navigator.share({ title, text });
  } catch (error) {
    if (!isAbortError(error)) {
      toast.error('Не удалось отправить');
    }
  }
}

function RequisitesTextBlock({
  definition,
  text,
}: {
  definition: TextBlockDefinition;
  text: string;
}) {
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  return (
    <section
      className="rounded-lg border border-border bg-card p-3"
      data-testid={`company-requisites-text-${definition.key}`}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="min-w-0 text-sm font-semibold leading-snug">{definition.title}</h3>
        <div className="flex shrink-0 gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={definition.copyLabel}
            title={definition.copyLabel}
            onClick={() => void copyText(text)}
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={definition.shareLabel}
            title={canShare
              ? definition.shareLabel
              : 'Системная отправка недоступна в этом браузере'}
            onClick={() => void shareText(definition.title, text)}
            disabled={!canShare}
          >
            <Share2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
        {text}
      </div>
    </section>
  );
}

function AdditionalDetail({
  label,
  value,
  wide = false,
}: {
  label: string;
  value: string | number | null | undefined;
  wide?: boolean;
}) {
  return (
    <div className={wide ? 'col-span-2' : undefined}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 whitespace-pre-wrap break-words text-sm">
        {value === null || value === undefined || value === '' ? '—' : value}
      </dd>
    </div>
  );
}

function AdditionalRequisitesDetails({ requisites }: { requisites: CompanyRequisites }) {
  return (
    <section className="rounded-lg border border-border bg-card p-3">
      <h3 className="mb-3 text-sm font-semibold">Дополнительные данные</h3>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
        <AdditionalDetail label="Сайт" value={requisites.website} />
        <AdditionalDetail
          label="Телефон в подвале бланка"
          value={requisites.phoneInternational}
        />
        <AdditionalDetail
          label="НДС"
          value={requisites.hasVat ? `${requisites.vatRate}%` : 'Без НДС'}
        />
        <AdditionalDetail
          label="Штрихкод"
          value={requisites.useBarcode ? 'Используется' : 'Не используется'}
        />
        <AdditionalDetail
          label="Освобождение от НДС"
          value={requisites.vatExemption}
          wide
        />
        <AdditionalDetail label="Отметка о печати" value={requisites.stampText} wide />
      </dl>
    </section>
  );
}

function RequisitesDetailsSheet({
  requisites,
  onOpenChange,
}: {
  requisites: CompanyRequisites | null;
  onOpenChange: (open: boolean) => void;
}) {
  const blocks = useMemo(
    () => requisites ? buildCompanyRequisitesTextBlocks(requisites) : null,
    [requisites],
  );

  return (
    <Sheet open={Boolean(requisites)} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="max-h-[92dvh] overflow-y-auto"
        data-testid="company-requisites-details-sheet"
      >
        {requisites && blocks && (
          <>
            <SheetHeader className="pr-8 text-left">
              <SheetTitle>{requisites.legalName}</SheetTitle>
              <SheetDescription>
                Действуют с {formatEffectiveDate(requisites.effectiveFrom)}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-4 space-y-3 pb-4">
              {TEXT_BLOCKS.map((definition) => (
                <RequisitesTextBlock
                  key={definition.key}
                  definition={definition}
                  text={blocks[definition.key]}
                />
              ))}
              <AdditionalRequisitesDetails requisites={requisites} />
              <CompanyRequisitesAssetsSection requisitesId={requisites.id} />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export function CompanyRequisitesPage() {
  const [selected, setSelected] = useState<CompanyRequisites | null>(null);
  const requisitesQuery = useQuery({
    queryKey: ['mobile-company-requisites'],
    queryFn: companyRequisitesApi.getAll,
  });

  const grouped = useMemo(() => {
    return selectMobileCompanyRequisites(requisitesQuery.data ?? []);
  }, [requisitesQuery.data]);

  return (
    <div className="min-h-screen bg-background">
      <Header
        title="Наши реквизиты"
        showBack
        backTo="/more"
        rightContent={
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Обновить реквизиты"
            onClick={() => void requisitesQuery.refetch()}
            disabled={requisitesQuery.isFetching}
          >
            {requisitesQuery.isFetching ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <RefreshCw className="h-5 w-5" />
            )}
          </Button>
        }
      />

      <main className="space-y-5 p-4 pb-24">
        {requisitesQuery.isLoading && (
          <div className="flex items-center justify-center py-16" aria-label="Загрузка реквизитов">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {requisitesQuery.isError && (
          <div className="flex flex-col items-center justify-center py-16 text-center" role="alert">
            <AlertCircle className="mb-4 h-12 w-12 text-destructive" />
            <p className="font-medium">Не удалось загрузить реквизиты</p>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              {errorMessage(requisitesQuery.error, 'Повторите попытку')}
            </p>
            <Button
              type="button"
              variant="outline"
              className="mt-4"
              onClick={() => void requisitesQuery.refetch()}
            >
              <RefreshCw className="h-4 w-4" />
              Повторить
            </Button>
          </div>
        )}

        {!requisitesQuery.isLoading && !requisitesQuery.isError && grouped.map((group) => (
          <section key={group.legalEntity} aria-labelledby={`requisites-${group.legalEntity}`}>
            <div className="mb-2 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              <h2 id={`requisites-${group.legalEntity}`} className="text-sm font-semibold">
                {LEGAL_ENTITY_LABELS[group.legalEntity]}
              </h2>
            </div>
            <div className="space-y-2">
              {group.rows.map((row) => {
                const effectiveDate = formatEffectiveDate(row.effectiveFrom);
                return (
                  <button
                    key={row.id}
                    type="button"
                    className="flex w-full items-center gap-3 rounded-lg border border-border bg-card p-4 text-left shadow-xs transition-colors hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:bg-accent"
                    aria-label={`Открыть реквизиты ${row.legalName}, действуют с ${effectiveDate}`}
                    data-testid="company-requisites-version-card"
                    onClick={() => setSelected(row)}
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <CalendarDays className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium leading-snug">{row.legalName}</div>
                      <div className="mt-1 text-sm text-muted-foreground">
                        Действуют с {effectiveDate}
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          </section>
        ))}

        {!requisitesQuery.isLoading && !requisitesQuery.isError && grouped.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Building2 className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-muted-foreground">Реквизиты не найдены</p>
          </div>
        )}
      </main>

      <RequisitesDetailsSheet
        requisites={selected}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      />
    </div>
  );
}
